import React, { useState } from 'react';
import './daily-fee.styles.scss';
import PageLayout from '../../components/PageLayout';
import useSWR from 'swr';
import {
  formatCurrencyV2,
  formatDate,
  getItemById,
  getQueryString,
  getStatusColor,
  hasExpired,
  isAdmin,
} from '../../utils/common.util';
import { useDebounceState } from '../../utils/useDebounceState';
import Combobox from '../../components/combobox/Combobox';
import {
  DatePicker,
  DatePickerInput,
  Pagination,
  Select,
  SelectItem,
} from 'carbon-components-react';
import CustomButton from '../../components/custom-button/custom-btn.component';
import { PlusCircle, Printer } from 'react-feather';
import { PrintView } from '../../components/PrintView/print-view.component';
import PrintOutHead from '../../components/PrintOutHead';
import { postMethod } from '../../api/common';
import { useCustomMutation } from '../../utils/useCustomMutation';
import MakeAdvancePayment from './MakeAdvancePayment';
import { ConfirmModal } from '../../components/modal/confirm-modal/ConfirmModal.component';
import { useUrlState } from '../../utils/useUrlState';
import { useIsAllowed } from '../../utils/useCurrentUser';
import { AdvancePagesList } from './arrears-page/AdvancePagesList';
import ExportReportButton from '../../components/ExportReportButton';

const initialData = {
  studentId: '',
  academicYearId: '',
  termId: '',
  classId: '',
  dailyBillingFeeId: '',
  status: '',
  dateFrom: '',
  dateTo: '',
};

export default function AdvancePaymentsPage() {
  const { isAllowed } = useIsAllowed();

  const [query, setQuery] = useUrlState(initialData);

  const { data: academicYears } = useSWR(
    'academic-year/list/?skip=0&take=100&query=',
  );
  const { data: terms } = useSWR(
    query.academicYearId
      ? `academic-year/terms/?academicYearId=${query.academicYearId}`
      : null,
  );
  const { data: classes } = useSWR('class/list');

  const { data: d } = useSWR(`daily-billing/fees/list`);
  const feeTypes = d?.dailyBillingFee ?? [];

  const [searchStudent, setSearchStudent] = useDebounceState('');
  const { data: s, isLoading: isStudentsLoading } = useSWR(
    `daily-billing/fees/assigned-students?${getQueryString({
      query: searchStudent,
      dailyBillingFeeId: query.dailyBillingFeeId,
    })}`,
  );
  const students = s?.response ?? [];

  const [paginate, setPaginate] = useState({
    page: 1,
    pageSize: 20,
  });
  const mainUrl = 'daily-billing/revenue/list-advance-payment';
  const {
    data,
    isLoading: isLoadingPayments,
    error,
    mutate: refetchData,
  } = useSWR(
    query.academicYearId
      ? `${mainUrl}?${getQueryString({
          ...query,
          take: paginate.pageSize,
          skip: paginate.page - 1,
        })}`
      : null,
  );
  const paymentsData = data?.payment ?? [];

  const {
    trigger,
    isMutating,
    error: reverseError,
    reset,
  } = useCustomMutation(
    `daily-billing/revenue/reverse-advance-payment`,
    (url, { arg }) => postMethod(url, arg),
  );

  const [isOpen, setIsOpen] = React.useState('');

  function isDisabled(item) {
    return hasExpired(item?.invoice?.gracePeriodEndDate);
  }

  function handleClose() {
    setIsOpen('');
    reset();
  }

  const add_advance = isAllowed(['DailyBillingFeesAdvancePaymentAccess']);

  return (
    <PageLayout
      breadcrumbs={[
        { label: 'Daily Fees', to: '/daily-fees' },
        { label: `Deposit Payments`, active: true },
      ]}
      title={
        <div className="d-flex align-items-center" style={{ gap: '30px' }}>
          <h1 className="h-20">Deposit Payments</h1>{' '}
          {add_advance && (
            <button
              className="text-12 text-primary d-flex align-items-center font-bold pointer clean-btn "
              style={{ gap: '4px' }}
              onClick={() => setIsOpen('advance')}
            >
              <PlusCircle size="14" strokeWidth="2px" />
              <span>MAKE DEPOSIT PAYMENT</span>
            </button>
          )}
          <AdvancePagesList />
        </div>
      }
      isLoading={isLoadingPayments}
    >
      {/* FILTERS  */}
      <p className="mt-30 text-12">
        Select at least the academic year to show report.
      </p>
      <div className="mt-10 card-row-4 gap-20">
        <Select
          labelText="Academic Year"
          onChange={(e) =>
            setQuery({ academicYearId: e.target.value, termId: '' })
          }
          value={query.academicYearId}
        >
          <SelectItem value="" text="" />
          {academicYears?.map((year) => (
            <SelectItem key={year.id} value={year.id} text={year.description} />
          ))}
        </Select>
        <Select
          labelText="Term"
          disabled={!query.academicYearId}
          onChange={(e) => setQuery({ termId: e.target.value })}
          value={query.termId}
        >
          <SelectItem value="" text="All" />
          {terms?.map((term) => (
            <SelectItem key={term.id} value={term.id} text={term.description} />
          ))}
        </Select>
        <DatePicker
          dateFormat="Y-m-d"
          datePickerType="single"
          id="start-date-picker"
          maxDate={new Date().toISOString().split('T')[0]}
          onChange={(value) => {
            if (value.length > 0) {
              setQuery({
                dateFrom: [
                  value[0].getFullYear(),
                  ('0' + (value[0].getMonth() + 1)).slice(-2),
                  ('0' + value[0].getDate()).slice(-2),
                ].join('-'),
              });
            } else setQuery({ dateFrom: '' });
          }}
          onClose={(value) => {}}
        >
          <DatePickerInput
            className="start-date"
            id="start-date"
            name="dateFrom"
            labelText="Start Date"
            value={query.dateFrom}
            max={new Date().toISOString().split('T')[0]}
            // pattern="d{1,2}/d{4}"
            pattern="\d{4}-\d{2}-\d{2}"
            autoComplete="off"
            placeholder="YYYY-MM-DD"
            type="text"
            onChange={(e) => setQuery({ dateFrom: e.target.value })}
          />
        </DatePicker>

        <DatePicker
          dateFormat="Y-m-d"
          datePickerType="single"
          id="end-date-picker"
          // minDate={
          //   new Date(query.dateFrom ?? '').toISOString().split('T')[0]
          // }
          maxDate={new Date().toISOString().split('T')[0]}
          onChange={(value) => {
            if (value.length > 0) {
              setQuery({
                dateTo: [
                  value[0].getFullYear(),
                  ('0' + (value[0].getMonth() + 1)).slice(-2),
                  ('0' + value[0].getDate()).slice(-2),
                ].join('-'),
              });
            } else setQuery({ dateTo: '' });
          }}
          onClose={(value) => {}}
        >
          <DatePickerInput
            className="end-date"
            id="end-date"
            name="dateTo"
            labelText="End Date"
            value={query.dateTo}
            max={new Date().toISOString().split('T')[0]}
            // pattern="d{1,2}/d{4}"
            pattern="\d{4}-\d{2}-\d{2}"
            autoComplete="off"
            placeholder="YYYY-MM-DD"
            type="text"
            onChange={(e) => setQuery({ dateTo: e.target.value })}
          />
        </DatePicker>
        <Select
          labelText="Class"
          onChange={(e) => setQuery({ classId: e.target.value })}
          value={query.classId}
        >
          <SelectItem value="" text="All" />
          {classes?.map((term) => (
            <SelectItem key={term.id} value={term.id} text={term.description} />
          ))}
        </Select>
        <Select
          labelText="Fee Type"
          onChange={(e) =>
            setQuery({ dailyBillingFeeId: e.target.value, studentId: '' })
          }
          value={query.dailyBillingFeeId}
        >
          <SelectItem selected value="" text="All" />
          {feeTypes?.map((item) => (
            <SelectItem
              key={item.id}
              text={
                item.name +
                (isDisabled(item) && item?.invoice?.status
                  ? ` - [${item.invoice.status}]`
                  : '')
              }
              value={item.id}
              disabled={isDisabled(item)}
            />
          ))}
        </Select>
        <Combobox
          options={students ?? []}
          label="Select a Student"
          disabled={!query.dailyBillingFeeId}
          value={
            query.studentId
              ? students?.find((s) => s.id === Number(query.studentId))
              : null
          }
          onChange={(option) => {
            setQuery({ studentId: option ? option.id : '' });
            setSearchStudent('');
          }}
          onSearch={setSearchStudent} // The combobox now just updates the search term state
          onClear={() => {
            setQuery({ studentId: '' });
            setSearchStudent('');
          }}
          loading={isStudentsLoading} // Pass the loading state down
          renderSelected={(option) => `${option.firstName} ${option.lastName}`}
          renderOption={(option) => `${option.firstName} ${option.lastName}`}
          labelKey="fullName"
          valueKey="id"
          searchPlaceholder="Search students..."
          placeholder=""
        />
        <Select
          labelText="Status"
          onChange={(e) => setQuery({ status: e.target.value, termId: '' })}
          value={query.status}
        >
          <SelectItem value="" text="All" />
          {['Reversed', 'Approved']?.map((item) => (
            <SelectItem key={item} value={item} text={item} />
          ))}
        </Select>
      </div>

      <div className="mt-20">
        {error && <p className="text-14 mt-10 text-danger">{error.message}</p>}
        {reverseError && (
          <p className="text-14 mt-10 text-danger">{reverseError.message}</p>
        )}
        {paymentsData?.length === 0 && (
          <p className="text-14 mt-30 text-grey">
            No deposit payments collected for the selected filters.
          </p>
        )}
        {paymentsData?.length > 0 && (
          <>
            <PrintOutHead
              title={`Daily Billing Deposit Payments`}
              headings={[
                {
                  label: 'Academic Year',
                  value:
                    getItemById(academicYears, query.academicYearId)
                      ?.description ?? 'All',
                },
                {
                  label: 'Term',
                  value: getItemById(terms, query.termId)?.description ?? 'All',
                },
                {
                  label: 'Class',
                  value:
                    getItemById(classes, query.classId)?.description ?? 'All',
                },
                {
                  label: 'Student',
                  value: query.studentId
                    ? students?.find((s) => s.id === query.studentId)?.fullName
                    : 'All',
                },
                {
                  label: 'Fee Type',
                  value:
                    getItemById(feeTypes, query.dailyBillingFeeId)?.name ??
                    'All',
                },
              ]}
            />
            <PrintView>
              <div style={{ overflow: 'auto' }}>
                <table className="bx--data-table table-white">
                  <thead>
                    <tr>
                      {/* <th>Unique Id</th> */}
                      <th>Payment Date</th>
                      <th>Name</th>
                      <th>Class</th>
                      <th>Fee Type</th>
                      <th>
                        Amount Paid <br /> (GH₵)
                      </th>
                      <th>
                        Amount Applied <br /> (GH₵)
                      </th>
                      <th>
                        Balance <br /> (GH₵)
                      </th>
                      <th>Payment Mode</th>

                      <th>Status</th>
                      {isAdmin() && <th className="no-print">Action</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {paymentsData?.map((entry, idx) => (
                      <tr key={entry.id}>
                        {/* <td>{entry.student.studentId}</td> */}
                        <td>{formatDate(entry.paymentDate)}</td>
                        <td>
                          {entry.student.firstName} {entry.student.lastName}
                        </td>
                        <td>{entry.student.classDescription}</td>
                        <td>{entry.revenue.dailyBillingFeeName}</td>
                        <td>{entry.amountPaid}</td>
                        <td>{entry.appliedDepositAmount}</td>
                        <td>{entry.depositBalance}</td>
                        <td>{entry.paymentMode}</td>

                        <td className={getStatusColor(entry?.status)}>
                          {entry.status}
                        </td>
                        {isAdmin() && (
                          <td className="no-print">
                            <button
                              className="clean-btn text-primary"
                              onClick={() => setIsOpen(entry)}
                              disabled={isMutating}
                            >
                              View
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="font-bold">
                      <td colSpan={4}>Total</td>
                      <td>
                        {paymentsData?.reduce(
                          (acc, cur) => acc + cur.amountPaid,
                          0,
                        )}
                      </td>
                      <td>
                        {paymentsData?.reduce(
                          (acc, cur) => acc + cur.appliedDepositAmount,
                          0,
                        )}
                      </td>
                      <td>
                        {paymentsData?.reduce(
                          (acc, cur) => acc + cur.depositBalance,
                          0,
                        )}
                      </td>
                      <td colSpan={3}></td>
                    </tr>
                  </tfoot>
                </table>
                <div className="no-print">
                  <Pagination
                    backwardText="Previous page"
                    forwardText="Next page"
                    page={paginate.page}
                    pageNumberText="Page Number"
                    pageSize={paginate.pageSize}
                    pageSizes={[10, 20, 30, 40, 50, 100]}
                    totalItems={data?.paginationInfo?.totalCount || 0}
                    onChange={(value) => setPaginate(value)}
                  />
                </div>
              </div>
            </PrintView>
          </>
        )}

        {paymentsData?.length > 0 && (
          <div className="mt-30 d-flex gap-20 align-items-center flex-end">
            <ExportReportButton
              apiUrl={`${mainUrl}/export?${getQueryString(query)}`}
              fileName={`Daily Fee Deposit Payments Report.xlsx`}
            />
            <CustomButton classes="secondary" onClick={() => window.print()}>
              <Printer size="16" strokeWidth="2px" /> &nbsp; Print
            </CustomButton>
          </div>
        )}
      </div>

      <ConfirmModal
        title={`Reverse ${formatCurrencyV2(isOpen?.amountPaid)} deposit payment?`}
        isOpen={typeof isOpen === 'object'}
        closeModal={() => setIsOpen('')}
        description={
          <>
            <p className="text-label">
              This action will reverse the deposit payment and all deductions on
              it. This action cannot be undone!
            </p>
            <div>
              <div
                style={{
                  fontSize: '12px',
                  marginTop: '20px',
                  display: 'grid',
                  gap: '10px',
                  gridTemplateColumns: '150px 1fr',
                }}
              >
                <span className="font-bold">Student Name:</span>
                <span>
                  {isOpen?.student?.firstName} {isOpen?.student?.lastName}
                </span>
                <span className="font-bold">Unique ID:</span>
                <span>{isOpen?.student?.studentId}</span>
                <span className="font-bold">Fee Type:</span>
                <span>{isOpen?.feeType}</span>
                <span className="font-bold">Amount Paid (GH₵):</span>
                <span>{formatCurrencyV2(isOpen?.amountPaid)}</span>
                <span className="font-bold">Description:</span>
                <span>{isOpen?.description}</span>
                <span className="font-bold">Payment Date:</span>
                <span>{formatDate(isOpen?.paymentDate)}</span>
              </div>
            </div>
          </>
        }
        actionText="REVERSE"
        onClick={
          isOpen?.status?.toUpperCase() === 'REVERSED'
            ? undefined
            : () =>
                trigger(
                  { revenueId: isOpen?.revenue?.id },
                  {
                    onSuccess: () => {
                      refetchData();
                      handleClose();
                    },
                  },
                )
        }
        isLoading={isMutating}
        errorMessage={reverseError ? reverseError.message : null}
        actionBtnClasses="primary"
      />

      <MakeAdvancePayment
        isOpen={isOpen === 'advance'}
        closeModal={() => handleClose()}
        onSuccess={() => refetchData()}
      />
    </PageLayout>
  );
}
